<template lang="pug">
q-dialog.history(v-model="show", transition-show="slide-left", transition-hide="slide-right", @hide="closeModal")
  .history-wrapper
    .spinner-container.history-spinner(v-if="loading")
      q-spinner(color="primary", size="3em")
    table.history-table(border="1", v-else)
      thead
        tr
          th(v-for="column in columns", :key="column.name", :style="column.column_style") {{ column.label }}
      tbody
        tr(v-for="item in tableData", :key="item.id")
          th(v-for="column in columns", :key="column.name + item.id") 
            div(v-if="column.name === 'changes'") 
              table.history-changes
                thead
                  tr
                    th.history-changes__name {{ locales.name }}
                    th.history-changes__was {{ locales.was }}
                    th.history-changes__became {{ locales.became }}
                tbody
                  tr(v-for="(value, name) in item['changes']", :key="value[0] + value[1] + name")
                    th {{ name }}
                    th {{ getTableValue(value[0]) }}
                    th {{ getTableValue(value[1]) }}
            div(v-else) {{ getTableValue(item[column.name]) }}
        .spinner-container(v-if="loadingNextPage")
          q-spinner(color="primary", size="3em")
        div(v-if="showObserver", v-observe-visibility="loadNextPage")
</template>

<script setup>
import { computed, ref, onBeforeMount, onMounted, provide } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import i18n from "@/plugins/vue-i18n";
import { currentLocale } from "@/services/useLocales";
import { format, parseISO } from "date-fns";

const props = defineProps({
  show: { type: Boolean, default: false },
  path: { type: String, default: "" },
});

const emit = defineEmits(["close"]);

const show = ref(props.show);
const columns = ref([]);
const tableData = ref([]);
const page = ref(1);
const perPage = 20;
const hasNextPage = ref(false);
const loading = ref(true);
const loadingNextPage = ref(true);

const path = computed(() => props.path);
const showObserver = computed(() => hasNextPage.value && !loadingNextPage.value);

const locales = i18n["messages"][currentLocale.value]["history"];

const closeModal = () => {
  emit("close");
};

const getTableValue = val => {
  // Use strict check to allow 0 as a valid value
  if (val === null || val === undefined || val === "") {
    return "-";
  }
  const isoDate = parseISO(val);
  if (isoDate.toString() !== "Invalid Date") {
    const userDateTime = new Date(isoDate);
    return `${userDateTime.toLocaleDateString("ru")} ${userDateTime.toLocaleTimeString("ru", { timeStyle: "short" })}`;
  }
  if (Array.isArray(val)) {
    return val.join(", ");
  }
  return val;
};

const loadNextPage = async isVisible => {
  if (isVisible) {
    loadingNextPage.value = true;
    page.value++;
    await loadData();
  }
};

const loadData = async () => {
  try {
    const params = {
      infinite_scroll: {
        per_page: perPage,
        page: page.value,
      },
    };
    const { data } = await backend.index(path.value, { params }, { encodeNestedData: true });
    if (columns.value.length === 0) {
      columns.value = data.columns;
    }
    tableData.value = [...tableData.value, ...data.data];

    // Получаем количество оставшихся не показанных элементов путем вычитания из общего их колличества произведения
    // текущего каунтера страницы на количество элементов в страницуе
    const remainingDataItems = data.count - page.value * perPage;
    // Проверяем что оставшихся элементов больше 0
    hasNextPage.value = remainingDataItems > 0;
  } catch (e) {
    await handleError(e);
  } finally {
    loading.value = false;
    loadingNextPage.value = false;
  }
};

onBeforeMount(async () => {
  await loadData();
});
</script>

<style scoped lang="scss">
.history-wrapper {
  width: 1400px;
  height: 800px;
  background-color: var(--field-background);
  padding: 30px;
  border-radius: 30px;
  border-color: var(--field-border);
  color: var(--dashboard-label-color);
  position: relative;

  &::-webkit-scrollbar-track {
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .history-spinner {
    top: 0;
    left: 0;
  }
}
.history-table {
  width: 100%;
  border-spacing: 0;
  border-left: 0;
  border-top: 0;

  thead {
    background-color: #5b7aff;
    color: #fff;
    height: 40px;
    border-color: #fff;
    border: 0;
    border-color: var(--dashboard-card-table-tr-background);
  }

  tbody {
    border: 0;
    border-color: var(--dashboard-card-table-tr-background);
  }

  .spinner-container {
    height: 100px;
    left: 0;
  }
}

.history-changes {
  width: 100%;

  &__name {
    width: 40%;
  }

  &__was {
    width: 30%;
  }

  &__became {
    width: 30%;
  }

  thead {
    height: 30px;
  }

  th {
    word-break: break-all;
  }
}
</style>
